import React, { useState } from "react";
import { ChevronDown, X, Filter, Download } from "lucide-react";
import { DateRangeFilter } from "../customers/customerDetail/DateRangeFilter";

interface CashRecordsFiltersProps {
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  selectedShift?: string;
  selectedPaymentType?: string;
  selectedExchangeType?: string;
  onDateChange?: (date: string) => void;
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
  onDateClear?: () => void;
  onShiftChange?: (shift: string) => void;
  onExchangeTypeChange?: (type: string) => void;
  onClearAll?: () => void;
  onExportExcel?: () => void;
}

export const CashRecordsFilters: React.FC<CashRecordsFiltersProps> = ({
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  selectedShift,
  selectedPaymentType,
  selectedExchangeType,
  onDateChange,
  onDateRangeChange,
  onDateClear,
  onShiftChange,
  onExchangeTypeChange,
  onClearAll,
  onExportExcel,
}) => {
  const [showShiftDropdown, setShowShiftDropdown] = useState(false);
  const [showExchangeTypeDropdown, setShowExchangeTypeDropdown] =
    useState(false);

  const shiftOptions = [
    "1AM",
    "2AM",
    "3AM",
    "4AM",
    "5AM",
    "6AM",
    "7AM",
    "8AM",
    "9AM",
    "10AM",
    "11AM",
    "12PM",
    "1PM",
    "2PM",
    "3PM",
    "4PM",
    "5PM",
    "6PM",
    "7PM",
    "8PM",
    "9PM",
    "10PM",
  ];
  const exchangeTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Cash-In", value: "cash_in" },
    { label: "Cash-Out", value: "cash_out" },
  ];

  const hasActiveFilters = Boolean(
    selectedDate ||
      (selectedDateFrom && selectedDateTo) ||
      selectedShift ||
      selectedPaymentType ||
      (selectedExchangeType && selectedExchangeType !== "all"),
  );

  const getFilterSummary = () => {
    const filters = [];

    if (selectedDate) {
      filters.push(`Date: ${new Date(selectedDate).toLocaleDateString()}`);
    } else if (selectedDateFrom && selectedDateTo) {
      filters.push(
        `Date: ${new Date(selectedDateFrom).toLocaleDateString()} - ${new Date(selectedDateTo).toLocaleDateString()}`,
      );
    }

    if (selectedShift) {
      filters.push(`Shift: ${selectedShift}`);
    }

    if (selectedPaymentType) {
      filters.push(`Payment Type: ${selectedPaymentType}`);
    }

    if (selectedExchangeType && selectedExchangeType !== "all") {
      const typeLabel =
        selectedExchangeType === "cash_in" ? "Cash-In" : "Cash-Out";
      filters.push(`Type: ${typeLabel}`);
    }

    return filters.length > 0 ? filters.join(" • ") : "No filters applied";
  };

  return (
    <div className="bg-slate-800 rounded-lg p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-center">
        <div className="flex gap-4 items-center flex-1">
          <div className="flex-1">
            <DateRangeFilter
              selectedDate={selectedDate}
              selectedDateFrom={selectedDateFrom}
              selectedDateTo={selectedDateTo}
              onDateChange={onDateChange}
              onDateRangeChange={onDateRangeChange}
              onClear={onDateClear}
            />
          </div>

          {/* Shift Filter */}
          <div className="relative flex-shrink-0 w-32">
            <button
              onClick={() => {
                setShowShiftDropdown(!showShiftDropdown);
                setShowExchangeTypeDropdown(false);
              }}
              className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors w-full"
            >
              <span className="truncate flex-1 text-left">
                {selectedShift || "All Shifts"}
              </span>
              {selectedShift && (
                <X
                  className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShiftChange?.("");
                  }}
                />
              )}
              <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
            </button>

            {showShiftDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 w-full max-h-64 overflow-y-auto">
                <button
                  onClick={() => {
                    onShiftChange?.("");
                    setShowShiftDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-600 text-white text-sm border-b border-slate-600 last:border-b-0"
                >
                  All Shifts
                </button>
                {shiftOptions.map((shift) => (
                  <button
                    key={shift}
                    onClick={() => {
                      onShiftChange?.(shift);
                      setShowShiftDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-600 text-white text-sm border-b border-slate-600 last:border-b-0"
                  >
                    {shift}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Exchange Type Filter */}
          <div className="relative flex-shrink-0 w-40">
            <button
              onClick={() => {
                setShowExchangeTypeDropdown(!showExchangeTypeDropdown);
                setShowShiftDropdown(false);
              }}
              className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors w-full"
            >
              <span className="truncate flex-1 text-left">
                {selectedExchangeType === "all" || !selectedExchangeType
                  ? "All Types"
                  : selectedExchangeType === "cash_in"
                    ? "Cash-In"
                    : "Cash-Out"}
              </span>
              {selectedExchangeType && selectedExchangeType !== "all" && (
                <X
                  className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExchangeTypeChange?.("all");
                  }}
                />
              )}
              <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
            </button>

            {showExchangeTypeDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 w-full max-h-64 overflow-y-auto">
                {exchangeTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onExchangeTypeChange?.(option.value);
                      setShowExchangeTypeDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-600 text-white text-sm border-b border-slate-600 last:border-b-0"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-shrink-0">
          <button
            onClick={onExportExcel}
            className="flex items-center gap-2 px-4 py-2 h-10 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title="Export to Excel"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors whitespace-nowrap"
          >
            <Filter className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-slate-600">
          <div className="text-sm text-slate-400">
            <span className="font-medium">Active Filters:</span>{" "}
            {getFilterSummary()}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {(showShiftDropdown || showExchangeTypeDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowShiftDropdown(false);
            setShowExchangeTypeDropdown(false);
          }}
        />
      )}
    </div>
  );
};
